import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { PostHogProvider } from "@/contexts/PostHogProvider";
import OnboardingGuard from "@/components/OnboardingGuard";
import Footer from "@/components/Footer";
import FeedbackWidget from "@/components/FeedbackWidget";
import { ContentSecurityPolicy } from "@/components/ui/content-security-policy";
import PageLoadingSpinner from "@/components/PageLoadingSpinner";
import AnalyticsIdentityBridge from "@/contexts/AnalyticsIdentityBridge";
import PasswordResetDialog from "@/components/PasswordResetDialog";
import ErrorBoundary from "@/components/ErrorBoundary";
import Index from "./pages/Index";

// Lazy load all pages except Index (landing page loads immediately)
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Journal = lazy(() => import("./pages/Journal"));
const EntryViewer = lazy(() => import("./pages/EntryViewer"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Profile = lazy(() => import("./pages/Profile"));
const Subscription = lazy(() => import("./pages/Subscription"));
const SubscriptionGate = lazy(() => import("./pages/SubscriptionGate"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Support = lazy(() => import("./pages/Support"));
const PrayerCollection = lazy(() => import("./pages/PrayerCollection"));
const JourneyPage = lazy(() => import("./pages/JourneyPage"));
const PrayerScreen = lazy(() => import("./pages/PrayerScreen"));
const ExplorerCompletionScreen = lazy(() => import("./components/journal/ExplorerCompletionScreen"));
const DepartureStep = lazy(() => import("./components/journal/DepartureStep"));

const Checkout = lazy(() => import("./pages/Checkout"));

// Eden Pages is a read-mostly app: server data (entries, scripture, profile)
// changes via explicit user action, not background events. A small staleTime
// keeps navigation snappy without holding stale data for long; disabling
// refetchOnWindowFocus avoids the jarring re-flash when users tab back from
// scripture or a prayer email.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function AppContent() {
  const location = useLocation();
  const isLandingPage = location.pathname === "/";

  return (
    <div className="min-h-screen flex flex-col">
      {/* Global ErrorBoundary protects every route from a render-time
          white-screen-of-death. Page-level boundaries inside Dashboard,
          Journal and JourneyPage still wrap their own subtree for
          finer-grained fallback UI. */}
      <ErrorBoundary>
        <Suspense fallback={<PageLoadingSpinner />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/journal" element={<Journal />} />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/dashboard" element={
              <OnboardingGuard>
                <Dashboard />
              </OnboardingGuard>
            } />
            <Route path="/entry/:id" element={
              <OnboardingGuard>
                <EntryViewer />
              </OnboardingGuard>
            } />
            <Route path="/profile" element={<Profile />} />
            <Route path="/subscription" element={
              <OnboardingGuard>
                <Subscription />
              </OnboardingGuard>
            } />
            <Route path="/subscription-gate" element={<SubscriptionGate />} />
            <Route path="/subscription/expired" element={<SubscriptionGate />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/support" element={<Support />} />
            <Route path="/prayers" element={<PrayerCollection />} />
            <Route path="/journal/:entryId/departure" element={<DepartureStep />} />
            <Route path="/journal/:entryId/prayer" element={<PrayerScreen />} />
            <Route path="/journal/:entryId/complete" element={<ExplorerCompletionScreen />} />
            <Route path="/journey" element={
              <OnboardingGuard>
                <JourneyPage />
              </OnboardingGuard>
            } />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
      {!isLandingPage && <Footer />}
      {!isLandingPage && <FeedbackWidget />}
    </div>
  );
}

function App() {
  return (
    <PostHogProvider>
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <ContentSecurityPolicy />
            <AnalyticsIdentityBridge />
            <PasswordResetDialog />
            <BrowserRouter>
              <Toaster position="top-center" richColors />
              <AppContent />
            </BrowserRouter>
          </TooltipProvider>
        </QueryClientProvider>
      </AuthProvider>
    </PostHogProvider>
  );
}

export default App;
